import { DateTime } from 'luxon';
import {
  AppointmentStatus,
  MAX_QUERY_PERIODS,
} from '~/lib/constants/calendar.constants';
import { ParticipantTypes } from '~/lib/constants/schedule.constants';
import {
  type Recurrence,
  filterSupportAppointments,
  getAppointmentEndTime,
  getPatientSupportFreePeriods,
} from '~/lib/util/calendar.util';
import { getPreferredFullName } from '~/lib/util/patient.util';
import { isNil } from 'lodash';
import type { Attendees } from '~/lib/types/appointment/Attendees';
import {
  AppointmentTypeName,
  MEDICAL_ASSISTANT_SESSION_DEFAULT_DURATION,
} from '~/lib/constants/scheduling.constants';

export enum EditSteps {
  RECURRENCE_SELECT = 'RECURRENCE_SELECT',
  SLOT_SELECT = 'SLOT_SELECT',
  APPOINTMENT_SUMMARY = 'APPOINTMENT_SUMMARY',
}
interface EligibleToEditAppointmentProps {
  appointmentTypes: AppointmentType[];
  scheduleItem: ScheduleItemBase;
}

type ScheduledAppointmentsBase<T> = {
  patientAppointments: T[];
  supportAppointments: T[];
};

export type ScheduledApppointmentsResponse =
  | AppointmentDetailResponse
  | AppointmentScheduleDatetimeResponse;

export type ScheduledAppointments =
  ScheduledAppointmentsBase<ScheduledApppointmentsResponse>;

export const getFinalPatientAppointments = (
  patientAttendee: Participant,
  patientAppointments: ScheduledAppointments['patientAppointments'],
  rescheduledAppointmentExternalId: string,
) => {
  return patientAttendee?.isChecked
    ? patientAppointments.filter(
        (each) =>
          each.appointmentExternalId !== rescheduledAppointmentExternalId,
      )
    : [];
};

export const getCaption = ({
  type,
  timezoneDetails,
}: {
  type: string;
  timezoneDetails: ProviderTimezone;
}) => {
  if (timezoneDetails?.label)
    return `${type}, ${timezoneDetails.label.split(' ')[0]}`;
  return type;
};

export const getQueryPeriodsForEditAppointment = ({
  editAppointment,
  scheduledAppointments,
  patientAttendee,
  supportIds,
}: {
  scheduledAppointments: ScheduledAppointments;
  patientAttendee: Participant;
  editAppointment: ScheduleItemBase;
  supportIds?: string[];
}) => {
  const { patientAppointments, supportAppointments } = scheduledAppointments;
  const start = DateTime.now();
  const end = start.plus({ months: 3 });
  const finalPatientAppointments = getFinalPatientAppointments(
    patientAttendee,
    patientAppointments,
    editAppointment.externalId,
  );

  const finalSupportAppointments = supportAppointments.filter(
    (eachAppointment) =>
      eachAppointment.appointmentExternalId !== editAppointment.externalId,
  );

  return getPatientSupportFreePeriods({
    end,
    patientAppointments: finalPatientAppointments,
    start,
    supportAppointments: filterSupportAppointments(
      supportIds ?? [],
      finalSupportAppointments,
    ),
  }).slice(0, MAX_QUERY_PERIODS);
};

export const getExternallyAddedProviders = ({
  careTeamResponse,
  attendees,
}: {
  careTeamResponse: CareTeamMember[];
  attendees: ScheduleUserBase[];
}) => {
  if (!careTeamResponse) return [];

  return attendees
    .filter((attendee) => attendee.providerType !== undefined)
    .filter(
      (attendee) =>
        !careTeamResponse.some(
          (provider) => provider.providerExternalId === attendee.externalId,
        ),
    );
};

export const getCheckedParticipants = (attendees: Participant[]) => {
  return (attendees ?? []).filter((attendee) => attendee.isChecked === true);
};

export const getAttendingParticipants = ({
  hostAttendee,
  patientAttendee,
  supportAttendees = [],
  providerAttendees,
  externalAttendees,
}: Attendees): ScheduleUserBase[] => {
  const createAttendee = (attendee?: Participant, isHost = false) => {
    if (!attendee) return null;

    let userType = attendee.secondaryText.split(',')[0];
    if (!isHost && attendee.type === ParticipantTypes.PATIENT) {
      userType = 'Patient';
    }

    return {
      ...attendee,
      externalId: attendee.id,
      isHost,
      name: attendee.primaryText,
      userType,
    };
  };

  const checkedProviders = getCheckedParticipants([
    ...providerAttendees,
    ...externalAttendees,
  ]);

  const checkedSupports = getCheckedParticipants(supportAttendees);

  return [
    createAttendee(hostAttendee, true),
    ...(patientAttendee?.isChecked ? [createAttendee(patientAttendee)] : []),
    ...checkedSupports.map((support) => createAttendee(support)),
    ...checkedProviders.map((provider) => createAttendee(provider)),
  ].filter(Boolean) as ScheduleUserBase[];
};

export const getEventTitle = ({
  newAppointmentType,
  selectedAppointmentType,
  editAppointment,
}: {
  newAppointmentType: AppointmentType | null;
  selectedAppointmentType: AppointmentType | null;
  editAppointment: ScheduleItemBase;
}) => {
  const appointmentType = newAppointmentType ?? selectedAppointmentType;
  if (!appointmentType) {
    const { title, duration } = editAppointment;
    return `${title}, ${duration} minutes`;
  }
  return `${appointmentType.appointmentType}, ${appointmentType.durationInMinutes} minutes`;
};

interface GetAppointmentDataParams {
  attendees: Attendees;
  appointmentType: AppointmentType;
  recurrenceFrequency: Recurrence;
  appointmentStartDateTime: string;
  ignoreAvailabilitySettings: boolean;
  editAppointment: ScheduleItemBase;
  patient: Patient;
}

export const getAppointmentData = ({
  attendees,
  appointmentType,
  recurrenceFrequency,
  appointmentStartDateTime,
  ignoreAvailabilitySettings,
  editAppointment,
  patient,
}: GetAppointmentDataParams) => {
  const now = DateTime.now();
  const localTimezone = now.zoneName;

  const { recurringUntilDateTime, externalId } = editAppointment;

  const {
    hostAttendee,
    patientAttendee,
    providerAttendees,
    supportAttendees,
    externalAttendees,
  } = attendees;
  const hostExternalId = hostAttendee?.id ?? null;
  const isPatientAttending = !!patientAttendee?.isChecked;

  const checkedProviderAttendees = getCheckedParticipants([
    ...providerAttendees,
    ...externalAttendees,
  ]);

  const checkedSupportAttendees = getCheckedParticipants(supportAttendees);

  const providerAttendeeExternalIds = checkedProviderAttendees.map(
    (provider) => provider.id,
  );
  const supportAttendeeExternalIds = checkedSupportAttendees.map(
    (support) => support.id,
  );

  const { appointmentTypeLabel, durationInMinutes } = appointmentType;

  return {
    appointmentEndDateTime: getAppointmentEndTime(
      appointmentStartDateTime,
      durationInMinutes,
    ),
    appointmentStartDateTime,
    appointmentType: appointmentTypeLabel,
    editAppointmentExternalId: externalId,
    hostExternalId,
    ignoreAvailabilitySettings,
    isPatientAttending,
    isRescheduleRequest: true,
    patientExternalId: patient.userId,
    providerAttendeeExternalIds,
    recurrenceFrequency,
    recurringAppointmentUntilDateTime: recurringUntilDateTime,
    status: AppointmentStatus.SCHEDULED,
    supportAttendeeExternalIds,
    timeZoneId: localTimezone,
  };
};

interface DefaultParticipantsProps {
  attendees: ScheduleUserBase[];
  selectedAppointmentType: AppointmentType;
  careTeamMemberCalendarAccounts?: CalendarAccount[];
  careTeamProviders: CareTeamMember[];
}

export const setDefaultHost = ({
  attendees,
  selectedAppointmentType,
  careTeamMemberCalendarAccounts,
  careTeamProviders,
}: DefaultParticipantsProps): Participant | null => {
  // TODO fix: selectedAppointmentType is required here, but it may be undefined in EditAppointment/index.tsx
  // Likely should wait until the API call comes back?
  const { providerTypeNames } = selectedAppointmentType;

  const host = attendees.find((a) => a.isHost);
  let eligibleProvider = careTeamProviders.find((p) =>
    providerTypeNames.includes(p.providerTypeName),
  );

  if (!eligibleProvider) {
    const { firstName, lastName, externalId, providerType, timezoneDetails } =
      attendees.find((a) => providerTypeNames.includes(a?.providerType)) || {};

    eligibleProvider = firstName
      ? {
          firstName,
          lastName,
          providerExternalId: externalId,
          providerTypeName: providerType,
          timezone: timezoneDetails?.label || '',
          userId: externalId,
        }
      : null;
  }

  const providerCalendar = careTeamMemberCalendarAccounts?.find(
    (c) => c.externalId === eligibleProvider?.providerExternalId,
  );
  const hostCalendar = careTeamMemberCalendarAccounts?.find(
    (c) => c.externalId === host?.externalId,
  );

  interface BuildParticipantParams {
    calendarId: string;
    externalId: string;
    sub: string;
    firstName: string;
    lastName: string;
    providerType: string;
    timezoneDetails: ProviderTimezone;
  }

  const buildParticipant = ({
    calendarId,
    externalId,
    sub,
    firstName,
    lastName,
    providerType,
    timezoneDetails,
  }: BuildParticipantParams): Participant => ({
    calendarId,
    id: externalId,
    isChecked: true,
    isEligibleToHost: providerTypeNames.includes(providerType),
    primaryText: getPreferredFullName(undefined, firstName, lastName),
    secondaryText: getCaption({ timezoneDetails, type: providerType }),
    sub,
  });

  if (host && providerTypeNames.includes(host.providerType)) {
    const {
      calendarId,
      externalId,
      sub,
      firstName,
      lastName,
      providerType,
      timezoneDetails,
    } = host;
    return buildParticipant({
      calendarId: calendarId ?? hostCalendar?.calendarId,
      externalId,
      firstName,
      lastName,
      providerType,
      sub: sub ?? hostCalendar?.sub,
      timezoneDetails,
    });
  }

  if (eligibleProvider && providerCalendar) {
    const { calendarId, sub } = providerCalendar;
    const {
      firstName,
      lastName,
      providerTypeName,
      timezone,
      providerExternalId,
    } = eligibleProvider;
    return buildParticipant({
      calendarId,
      externalId: providerExternalId,
      firstName,
      lastName,
      providerType: providerTypeName,
      sub,
      timezoneDetails: { code: timezone },
    });
  }

  return null;
};

export const isEligibleToEditAppointment = ({
  appointmentTypes,
  scheduleItem,
}: EligibleToEditAppointmentProps): boolean => {
  const { title, duration, attendees } = scheduleItem;

  const { providerType: hostProviderType } = attendees.find(
    (eachAttendee: ScheduleUserBase) => eachAttendee.isHost,
  );

  const selectedAppointmentType = appointmentTypes?.find(
    (eachAppointment) =>
      eachAppointment.appointmentType === title &&
      eachAppointment.durationInMinutes === duration,
  );

  return (
    selectedAppointmentType?.providerTypeNames?.includes(hostProviderType) ??
    false
  );
};

export const suppressMilliseconds = (dateTimeString?: string): string => {
  if (isNil(dateTimeString)) {
    return dateTimeString;
  }

  const date = new Date(dateTimeString);
  if (Number.isNaN(date.getTime())) {
    console.warn(`Invalid dateTimeString provided: ${dateTimeString}`);
    return dateTimeString;
  }
  date.setMilliseconds(0);
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
};

export const getIncludableAvailableSlots = ({
  editAppointment,
  appointmentRecurrenceSlots,
}: {
  editAppointment: ScheduleItemBase;
  appointmentRecurrenceSlots?: AppointmentScheduleDatetimeResponse[];
}): AvailabilitySlot[] => {
  const { attendees } = editAppointment;

  const participantSubs = (attendees ?? [])
    .filter((attendee) => attendee.sub)
    .map((attendee) => ({
      sub: attendee.sub,
    }));

  return (appointmentRecurrenceSlots ?? []).map((slot) => ({
    end: suppressMilliseconds(slot.appointmentEndDateTime),
    participants: participantSubs,
    start: suppressMilliseconds(slot.appointmentStartDateTime),
  }));
};

export const filterPatientAppointmentsWithEditingEventId = ({
  patientAppointments,
  eventId,
}: {
  patientAppointments: ScheduledApppointmentsResponse[];
  eventId?: string;
}): ScheduledApppointmentsResponse[] => {
  if (!eventId) return patientAppointments;
  return patientAppointments.filter(
    (appointment) => appointment.eventId !== eventId,
  );
};

export const hasPatient = ({
  attendingParticipants,
  updatedAttendees,
}: {
  attendingParticipants: ScheduleUserBase[] | null;
  updatedAttendees: ScheduleUserBase[] | null;
}): boolean => {
  const participants = attendingParticipants ?? updatedAttendees;

  return !!participants?.some(
    ({ type }) =>
      typeof type === 'string' &&
      type.toLowerCase() === ParticipantTypes.PATIENT.toLowerCase(),
  );
};

export const getParticipatingProviderCalendarInfo = (
  attendees: Participant[],
  hostAttendee: Participant,
) => {
  return attendees
    .filter(
      (attendee) =>
        attendee.sub !== undefined &&
        attendee.sub !== hostAttendee?.sub &&
        attendee.isChecked,
    )
    .map((provider) => ({
      calendarId: provider.calendarId,
      sub: provider.sub,
    }));
};

export const isNewDateGreaterThanOldDate = (
  newDateString: string | null | undefined,
  oldDateString: string | null | undefined,
): boolean => {
  if (!newDateString || !oldDateString) return false;

  const newDate = new Date(newDateString);
  const oldDate = new Date(oldDateString);

  if (Number.isNaN(newDate.getTime()) || Number.isNaN(oldDate.getTime()))
    return false;

  return newDate.getTime() > oldDate.getTime();
};

export const getIntervalInMinutesByAppointmentType = (
  selectedAppointmentDetails: AppointmentType,
): number => {
  const { appointmentType, durationInMinutes } =
    selectedAppointmentDetails ?? {};

  const is15MinutesStartIntervalSession =
    appointmentType === AppointmentTypeName.MEDICAL_ASSISTANT_SESSION ||
    (appointmentType === AppointmentTypeName.TREATMENT_TEAM_HUDDLE &&
      durationInMinutes === MEDICAL_ASSISTANT_SESSION_DEFAULT_DURATION);

  return is15MinutesStartIntervalSession
    ? MEDICAL_ASSISTANT_SESSION_DEFAULT_DURATION
    : undefined;
};
